/**
 * Export aggregate data as CSV files (one section per export call).
 *
 * Returns a CSV string. Caller handles download (Blob → URL → anchor click).
 */
import type { ConsultasAggregate } from "./aggregateConsultas";

function escapeCsv(value: unknown): string {
  if (value == null) return "";
  const s = String(value);
  if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(headers: string[], rows: unknown[][]): string {
  const head = headers.map(escapeCsv).join(",");
  const body = rows.map((r) => r.map(escapeCsv).join(",")).join("\n");
  return `${head}\n${body}`;
}

export function aggregateToCsv(agg: ConsultasAggregate): string {
  const sections: string[] = [];

  sections.push(`# Resumo`);
  sections.push(
    rowsToCsv(
      ["metric", "value"],
      [
        ["total_consultas", agg.total],
        ["com_analise_valida", agg.comAnaliseValida],
        ["intervalo_inicio", agg.intervaloDatas.inicio ?? ""],
        ["intervalo_fim", agg.intervaloDatas.fim ?? ""],
        ["method_total_avg", agg.medias.methodTotalAvg ?? ""],
        ["score_final_avg", agg.medias.scoreFinalAvg ?? ""],
      ],
    ),
  );

  sections.push(`\n# Personalidades`);
  sections.push(
    rowsToCsv(
      ["codigo", "nome", "centro_inteligencia", "count", "pct"],
      agg.personalidades.map((p) => [
        p.codigo,
        p.nome,
        p.centroIntel ?? "",
        p.count,
        p.pct.toFixed(2),
      ]),
    ),
  );

  sections.push(`\n# Flags Frequentes`);
  sections.push(
    rowsToCsv(
      ["flag", "count", "impacto_alto", "impacto_medio", "impacto_baixo", "exemplo"],
      agg.flags.map((f) => [
        f.flag,
        f.count,
        f.impactoAlto,
        f.impactoMedio,
        f.impactoBaixo,
        f.exemplos[0] ?? "",
      ]),
    ),
  );

  sections.push(`\n# Etapas Críticas`);
  sections.push(
    rowsToCsv(
      ["etapa", "count", "pct"],
      agg.etapaCriticaFreq.map((e) => [e.etapa, e.count, e.pct.toFixed(2)]),
    ),
  );

  sections.push(`\n# Gargalos`);
  sections.push(
    rowsToCsv(
      ["etapa", "count", "exemplo"],
      agg.gargalos.map((g) => [g.etapa, g.count, g.exemplos[0] ?? ""]),
    ),
  );

  sections.push(`\n# Médias por Etapa`);
  sections.push(
    rowsToCsv(
      ["etapa", "media_score"],
      Object.entries(agg.medias.porEtapa).map(([e, v]) => [e, (v ?? 0).toFixed(2)]),
    ),
  );

  sections.push(`\n# Distribuição de Grades`);
  sections.push(
    rowsToCsv(
      ["grade", "count"],
      Object.entries(agg.medias.gradeDistribution).map(([g, c]) => [g, c ?? 0]),
    ),
  );

  sections.push(`\n# Campo Semântico — Dor`);
  sections.push(
    rowsToCsv(
      ["termo", "total_freq", "consultas", "carga_predominante", "exemplo"],
      agg.campoSemantico.dor.map((t) => [
        t.termo,
        t.totalFreq,
        t.consultas,
        t.cargaPredominante,
        t.exemploFrase ?? "",
      ]),
    ),
  );

  sections.push(`\n# Campo Semântico — Desejo`);
  sections.push(
    rowsToCsv(
      ["termo", "total_freq", "consultas", "carga_predominante", "exemplo"],
      agg.campoSemantico.desejo.map((t) => [
        t.termo,
        t.totalFreq,
        t.consultas,
        t.cargaPredominante,
        t.exemploFrase ?? "",
      ]),
    ),
  );

  sections.push(`\n# Campo Semântico — Objeção`);
  sections.push(
    rowsToCsv(
      ["termo", "total_freq", "consultas", "carga_predominante", "exemplo"],
      agg.campoSemantico.objecao.map((t) => [
        t.termo,
        t.totalFreq,
        t.consultas,
        t.cargaPredominante,
        t.exemploFrase ?? "",
      ]),
    ),
  );

  sections.push(`\n# Expressões Marcantes`);
  sections.push(
    rowsToCsv(
      ["expressao", "count"],
      agg.campoSemantico.expressoesMarcantes.map((e) => [e.texto, e.count]),
    ),
  );

  sections.push(`\n# Palavras-chave Centrais`);
  sections.push(
    rowsToCsv(
      ["termo", "count"],
      agg.campoSemantico.palavrasChaveCentrais.map((p) => [p.termo, p.count]),
    ),
  );

  sections.push(`\n# Sazonalidade — Por Mês`);
  sections.push(
    rowsToCsv(
      ["mes", "count"],
      agg.sazonalidade.porMes.map((m) => [m.mes, m.count]),
    ),
  );

  sections.push(`\n# Sazonalidade — Dia da Semana`);
  sections.push(
    rowsToCsv(
      ["dia", "count"],
      agg.sazonalidade.porDiaSemana.map((d) => [d.dia, d.count]),
    ),
  );

  return sections.join("\n");
}

export function downloadAggregateCsv(agg: ConsultasAggregate, filename = "consultas-insights.csv") {
  const csv = aggregateToCsv(agg);
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
